/**
 * Backend region of the web app, derived from the customer's selected country.
 *
 * Each country document may carry a `regionConfig` (maintained with the mobile app):
 *   { group, region, functionPrefix, databaseId, graphqlBaseUrl, promoBaseUrl, walletBaseUrl }
 * - No `regionConfig`  → the home stack (the existing NEXT_PUBLIC_* env config); group 'home'.
 * - Complete config    → that region's stack. Countries with the same `group` share one stack.
 * - Incomplete config  → invalid: the country is not selectable (never silently mapped to another region).
 *
 * The region is fixed for the lifetime of a page load: Firebase, Apollo and the promo client are created once
 * for it. Changing to a country in another group saves the choice and reloads (see switchCountry).
 * With no valid selected country there is NO region: the app shows only the country picker (RegionGate) and
 * must not call any regional backend.
 *
 * Kept free of app imports: it is loaded by firebase.config.js before anything else.
 */
const ACCOUNT_DATA_KEY = 'accountData'; // localStorageKeys.ACCOUNT_DATA
const AUTH_DATA_KEY = 'authData'; // localStorageKeys.AUTH_DATA

export const HOME_GROUP = 'home';

const REQUIRED_FIELDS = ['group', 'region', 'functionPrefix', 'databaseId', 'graphqlBaseUrl', 'promoBaseUrl'];

const HOME_REGION = Object.freeze({
    group: HOME_GROUP,
    isHome: true,
    // Unchanged from before regions existed.
    functionsRegion: process.env.NEXT_PUBLIC_FUNCTION_REGION || 'us-central1',
    functionPrefix: 'api',
    databaseId: null, // default database
    graphqlUrl: process.env.NEXT_PUBLIC_GRAPHQL_URL,
    promoUrl: process.env.NEXT_PUBLIC_PROMO_API_URL || process.env.NEXT_PUBLIC_API_URL,
});

const isNonEmptyString = (value) => typeof value === 'string' && value.trim() !== '';

/** Whether a country document can be selected: no regionConfig (home) or a complete one. */
export const isCountrySelectable = (country) => {
    if (!country) return false;
    const config = country.regionConfig;
    if (config === undefined || config === null) return true;
    return REQUIRED_FIELDS.every((field) => isNonEmptyString(config[field]));
};

/** Region for a country document, or null when there is no country or its regionConfig is incomplete. */
export const regionForCountry = (country) => {
    if (!isCountrySelectable(country)) return null;
    const config = country.regionConfig;
    if (!config) return HOME_REGION;
    return Object.freeze({
        group: config.group,
        isHome: false,
        functionsRegion: config.region,
        functionPrefix: config.functionPrefix,
        databaseId: config.databaseId,
        graphqlUrl: config.graphqlBaseUrl,
        promoUrl: config.promoBaseUrl,
    });
};

/** Group of a country (missing regionConfig → home); null when the country is not selectable. */
export const groupOfCountry = (country) => regionForCountry(country)?.group ?? null;

const readJson = (key) => {
    if (typeof window === 'undefined') return null;
    try {
        const item = window.localStorage.getItem(key);
        return item ? JSON.parse(item) : null;
    } catch {
        return null;
    }
};

const writeJson = (key, value) => {
    window.localStorage.setItem(key, JSON.stringify(value));
    return window.localStorage.getItem(key) === JSON.stringify(value); // read-back: storage may be blocked
};

/** The selected country saved in this browser (the whole country document, incl. regionConfig), or null. */
export const getStoredCountry = () => readJson(ACCOUNT_DATA_KEY)?.selectedCountry || null;

/**
 * The region this page load runs in, or null (no valid selected country → RegionGate).
 * On the server (prerender) there is no selection; regional clients are never used there.
 */
export const currentRegion = regionForCountry(getStoredCountry());

/** Throw instead of silently calling the wrong backend when no region is selected. */
export const assertRegion = (what) => {
    if (!currentRegion) throw new Error(`No region selected: refusing to call ${what}`);
    return currentRegion;
};

/** Deployed Cloud Function name for this region, e.g. api-store-nearByStore → api-uk-store-nearByStore. */
export const regionalFunctionName = (name) => {
    const region = assertRegion(name);
    return region.isHome ? name : name.replace(/^api(?=-)/, region.functionPrefix);
};

// accountData entries that belong to one region's data (stores, promos, coins) and are dropped on a switch.
// The order type inside selectedAddress is kept.
const clearRegionScopedAccountData = (accountData) => {
    const next = { ...accountData };
    const orderType = next.selectedAddress?.type;
    delete next.selectedAddress;
    if (orderType) next.selectedAddress = { type: orderType };
    delete next.coupon;
    delete next.biteCoinStatus;
    return next;
};

/**
 * Save the selected country after the customer picks it.
 * Returns:
 *  - 'saved'     same group as the current region: saved as before, no reload (caller continues as usual)
 *  - 'reloading' first selection or a different group: region data cleared, signed out if needed, reloading
 * Throws when the country is not selectable or the choice can't be persisted (never continues in a wrong region).
 *
 * @param {object} country - country document (as listed by getCountries, with isoCode)
 * @param {object} options
 * @param {Function} options.signOut - async function signing the user out (Firebase)
 * @param {string} [options.homePath='/'] - where to land after the reload
 */
export const switchCountry = async (country, { signOut, homePath = '/' }) => {
    const nextRegion = regionForCountry(country);
    if (!nextRegion) throw new Error(`Country ${country?.isoCode || country?.id} has an incomplete region config`);

    const accountData = readJson(ACCOUNT_DATA_KEY) || {};
    if (currentRegion && currentRegion.group === nextRegion.group) {
        return 'saved'; // same stack: caller saves through its usual path
    }

    // First selection (no region yet) or a cross-region switch: the new region has its own customer profile,
    // cart and orders, so sign out (they sign in again there) and drop the old region's selections.
    const authData = readJson(AUTH_DATA_KEY);
    if (authData?.userId) {
        await signOut();
        try {
            window.localStorage.removeItem(AUTH_DATA_KEY);
            window.localStorage.removeItem('verifiedUser');
            window.localStorage.removeItem('verificationId');
        } catch {
            // storage unavailable — the writeJson check below fails and stops the switch
        }
    }
    try {
        window.sessionStorage.removeItem('promoCodeInfo');
        window.localStorage.removeItem('promoCodeInfo');
    } catch {
        // storage unavailable — handled by the check below
    }

    const saved = (() => {
        try {
            return writeJson(ACCOUNT_DATA_KEY, {
                ...clearRegionScopedAccountData(accountData),
                selectedCountry: country,
            });
        } catch {
            return false;
        }
    })();
    if (!saved) throw new Error('Could not save the selected country (site storage is blocked).');

    window.location.assign(homePath);
    return 'reloading';
};

// Field-by-field (not JSON.stringify): key order of Firestore maps is not guaranteed.
const CONFIG_FIELDS = [...REQUIRED_FIELDS, 'walletBaseUrl'];
const sameRegionConfig = (a, b) => {
    if (!a || !b) return !a && !b;
    return CONFIG_FIELDS.every((field) => (a[field] ?? null) === (b[field] ?? null));
};

/**
 * Compare the saved country with the live countries list (called once the list loads).
 * Returns:
 *  - 'ok'        still listed with the same regionConfig (or no country saved: RegionGate handles that)
 *  - 'reloading' removed/hidden/now invalid → selection cleared; or its regionConfig changed → updated; reloading
 */
export const revalidateStoredCountry = (liveCountries = []) => {
    const stored = getStoredCountry();
    if (!stored) return 'ok';
    const live = liveCountries.find((country) => country.id === stored.id);
    const sameConfig = sameRegionConfig(live?.regionConfig, stored.regionConfig);
    if (live && isCountrySelectable(live) && sameConfig) return 'ok';

    const accountData = readJson(ACCOUNT_DATA_KEY) || {};
    let next;
    if (live && isCountrySelectable(live) && groupOfCountry(live) === groupOfCountry(stored)) {
        // Same group, connection details changed: keep the selection, rebuild clients with the new config.
        next = { ...accountData, selectedCountry: { ...stored, regionConfig: live.regionConfig } };
    } else {
        // Removed, hidden, invalid, or moved to another group: ask again (RegionGate) after the reload.
        next = clearRegionScopedAccountData(accountData);
        delete next.selectedCountry;
    }
    let saved = false;
    try {
        saved = writeJson(ACCOUNT_DATA_KEY, next);
    } catch {
        saved = false;
    }
    if (!saved) {
        // Reloading without a persisted change would reload forever; report instead.
        console.error('Selected country is out of date but could not be updated (site storage is blocked).');
        return 'ok';
    }
    window.location.reload();
    return 'reloading';
};
